import * as React from "react";
import { useSearchParams } from "react-router-dom";

import type { ActiveSelection } from "../atoms/activeSelection";

type Args = {
  activeSelection: ActiveSelection;
  setActiveSelection: (selection: ActiveSelection) => void;
  paused?: boolean;
};

export function useActiveSelectionSearchParams({
  activeSelection,
  setActiveSelection,
  paused = false,
}: Args) {
  const [searchParams, setSearchParams] = useSearchParams();

  const activeStopParam = searchParams.get("aktivHallplats");

  const activeLineParam = searchParams.get("aktivLinje");

  // URL -> activeSelection
  React.useEffect(() => {
    if (paused) return;

    const current = activeSelectionRef.current;

    const nextSelection: ActiveSelection = activeStopParam
      ? {
          kind: "stop",
          id: String(activeStopParam),
        }
      : activeLineParam
      ? {
          kind: "line",
          id: String(activeLineParam),
        }
      : null;

    const isSameSelection =
      current === null && nextSelection === null
        ? true
        : current !== null &&
          nextSelection !== null &&
          current.kind === nextSelection.kind &&
          String(current.id) === String(nextSelection.id);

    if (!isSameSelection) {
      setActiveSelection(nextSelection);
    }
  }, [paused, activeStopParam, activeLineParam, setActiveSelection]);

  const activeSelectionRef = React.useRef(activeSelection);
  activeSelectionRef.current = activeSelection;

  // Active stop -> URL
  const setActiveStopParam = React.useCallback(
    (id: string | null) => {
      setSearchParams(
        (previous) => {
          const next = new URLSearchParams(previous);

          if (id) {
            next.delete("aktivLinje");
            next.set("aktivHallplats", String(id));
          } else {
            next.delete("aktivHallplats");
          }

          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  // Active line -> URL
  const setActiveLineParam = React.useCallback(
    (id: string | null) => {
      setSearchParams(
        (previous) => {
          const next = new URLSearchParams(previous);

          if (id) {
            next.delete("aktivHallplats");
            next.set("aktivLinje", String(id));
          } else {
            next.delete("aktivLinje");
          }

          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const clearActiveParams = React.useCallback(() => {
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);

        next.delete("aktivHallplats");
        next.delete("aktivLinje");

        return next;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  return {
    searchParams,
    setSearchParams,

    activeStopParam,
    activeLineParam,

    setActiveStopParam,
    setActiveLineParam,
    clearActiveParams,
  };
}
